import React, { useState } from 'react';
import { useFitStore } from '../store/useFitStore';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const Biometrics: React.FC = () => {
  const { user, clients, syncWearableData, calculateRecovery, showToast } = useFitStore();
  const [isSyncing, setIsSyncing] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  
  const clientData = clients.find(c => c.id === user?.id);
  const data = clientData?.biometrics?.[today];
  const recovery = calculateRecovery(today);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncWearableData();
      showToast('Biometrics updated successfully!');
    } catch (err: any) {
      showToast(err.message, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="main-content">
      <header style={{ marginBottom: 'var(--spacing-xl)' }}>
        <h1 className="text-accent" style={{ fontSize: '2rem' }}>Health Hub</h1>
        <p className="text-muted">Biometric intelligence from your wearables.</p>
      </header>

      {/* RECOVERY SCORE GAUGE */}
      <Card variant="glass" style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)', border: `2px solid var(--status-${recovery.status})` }}>
        <p className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase' }}>Daily Readiness</p>
        <div style={{ margin: '16px 0', position: 'relative', display: 'inline-block' }}>
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke="var(--bg-tertiary)" strokeWidth="8" />
            <circle cx="60" cy="60" r="50" fill="none" 
              stroke={`var(--status-${recovery.status})`} 
              strokeWidth="8" 
              strokeDasharray={314}
              strokeDashoffset={314 - (314 * recovery.score) / 100}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 1s ease-out', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
            />
          </svg>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <span style={{ fontSize: '2rem', fontWeight: 900 }}>{recovery.score}</span>
          </div>
        </div>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: `var(--status-${recovery.status})`, textTransform: 'uppercase', marginBottom: '8px' }}>
          {recovery.status} Recovery
        </h3>
        <p style={{ fontSize: '0.9rem', lineHeight: 1.4 }}>{recovery.recommendation}</p>
      </Card>

      {/* BIOMETRIC STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-xl)' }}>
        <Card padding="var(--spacing-md)">
          <span style={{ fontSize: '1.5rem' }}>👣</span>
          <p style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '8px' }}>{data?.steps?.toLocaleString() || '--'}</p>
          <p className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 800 }}>STEPS</p>
        </Card>
        <Card padding="var(--spacing-md)">
          <span style={{ fontSize: '1.5rem' }}>😴</span>
          <p style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '8px' }}>{data?.sleepHours || '--'}h</p>
          <p className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 800 }}>SLEEP</p>
        </Card>
        <Card padding="var(--spacing-md)">
          <span style={{ fontSize: '1.5rem' }}>❤️</span>
          <p style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '8px' }}>{data?.restingHR || '--'} bpm</p>
          <p className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 800 }}>RESTING HR</p>
        </Card>
        <Card padding="var(--spacing-md)">
          <span style={{ fontSize: '1.5rem' }}>📉</span>
          <p style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '8px' }}>{data?.sleepQuality || '--'}%</p>
          <p className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 800 }}>SLEEP QUALITY</p>
        </Card>
      </div>

      <Button fullWidth onClick={handleSync} isLoading={isSyncing}>
        SYNC WITH APPLE HEALTH
      </Button>
      
      <p className="text-muted" style={{ fontSize: '0.7rem', textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
        Last sync: {data?.lastSync ? new Date(data.lastSync).toLocaleTimeString() : 'Never'}
      </p>
    </div>
  );
};

export default Biometrics;
